/**
 * Legacy peer implementation using @playcastdotio/Peer.
 *
 * This module is only active when USE_NEW_PEER is false.
 * Extracted from peer.ts to keep the facade under the 500-line limit.
 */

import { createClient, PlaycastClientPeerStatus } from '@playcastdotio/Peer';
import { PlaycastPeerClient } from '@playcastdotio/Peer';
import { generators, PeerIdType, PeerMessageType, websocket } from '@playcastdotio/Messaging';
import { sendWebRtcStatsGatheredEvent } from '../peerTelemetry/playerConnectionTelemetry';
import type { WebrtcQualityStatsMetric } from '../types/webrtcTypes';
import { isAppleDevice, isMobileDevice } from '../../utility/utility';
import * as realtime from './realtimeMessaging';
import { getPersistentPlayerId } from '..';
import { handleHostMessage } from './hostMessaging/handleMessages';
import { invitationState, playerNameState } from '../stores/lobbyStores';
import { authStore } from '../stores/globalStores';
import { playerConnectionState, mediaStreamState } from '../stores/playerStores';
import { qualityManager, recordDisconnectReason } from './peer';

let peer: PlaycastPeerClient;

const sendVideoTransitTimingData = (stage: 'player_receive') => {
  const baseMessage = {
    header: {
      source: 'player' as const, target: 'system' as const,
      action: 'videoTransitTiming' as const, isReply: false,
      hostId: '', userAuth: authStore.currentState.playerAuthId,
    },
    body: { message: {}, timings: [], stats: [], echo: false },
    signature: '',
  } as any;

  generators.addTiming(baseMessage, {
    timestamp: performance.now(),
    description: `video_transit_${stage}`,
  }, 'player', { stage, transit_timestamp: performance.now().toString() });
};

export async function createPeerLegacy(hostBroadcastId: string): Promise<PlaycastPeerClient> {
  if (peer) {
    if (peer.status.currentState.connectionState === 'signalling') {
      console.warn('Peer is already connecting');
      return peer;
    }
    peer.status.removeListener();
    peer.close('player:close:duplicate_peer');
  }

  playerConnectionState.updateState({
    ...playerConnectionState.currentState,
    status: 'connecting',
    playerPeerId: '',
  });

  const playerId = await new Promise<string>((resolve, reject) => {
    realtime.sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_GET_ID, { peerIdType: PeerIdType.PLAYER }),
    );
    const timeout = setTimeout(() => reject('Peer ID timeout'), 5000);
    const listener = playerConnectionState.addListener((state) => {
      if (state.playerPeerId !== '') {
        clearTimeout(timeout);
        playerConnectionState.removeListener(listener);
        resolve(state.playerPeerId);
      }
    });
  });

  const iceServers = playerConnectionState.currentState.iceServers;

  peer = createClient(
    {
      realtimeApiManager: { send: (message) => realtime.sendMessage(message) },
      additionalMetadata: {
        displayName: playerNameState.currentState.name,
        playerPersistentId: getPersistentPlayerId(),
        email: authStore.currentState.email,
        playerAuthId: authStore.currentState.playerAuthId,
        invitationPlayerId:
          invitationState.currentState.invitationPlayerId || authStore.currentState.playerAuthId || getPersistentPlayerId(),
        isAppleDevice: isAppleDevice(),
        isMobileDevice: isMobileDevice(),
      },
      iceServers: iceServers || undefined,
      forceTurn: !playerConnectionState.currentState.allowPeerConnections,
    },
    playerId,
  );
  peer.connect(hostBroadcastId);

  try {
    (window as any).playcast = (window as any).playcast || {};
    (window as any).playcast.peer = peer;
    (window as any).playcast.qualityManager = qualityManager;
    (window as any).playcast.debugAudio = () => {
      const streams = mediaStreamState.currentState;
      console.log('=== AUDIO DEBUG INFO ===');
      console.log('MediaStream state:', {
        mainStream: streams.stream ? { id: streams.stream.id, audioTracks: streams.stream.getAudioTracks().length } : 'missing',
        audioStream: streams.audioStream ? { id: streams.audioStream.id, audioTracks: streams.audioStream.getAudioTracks().length } : 'missing',
      });
    };
  } catch (e) {
    console.error('Could not set window.playcast', e);
  }

  let statsCollectionArray: WebrtcQualityStatsMetric[] = [];

  const checkStatus = (status: PlaycastClientPeerStatus) => {
    if (status.connectionState === 'closed' || status.connectionState === 'refused') {
      const wasRefused = status.connectionState === 'refused';
      try { recordDisconnectReason(status.kickReason ?? 'unknown', 'marketing-demo:peerStatus'); } catch {}
      playerConnectionState.updateState({
        ...playerConnectionState.currentState,
        status: wasRefused ? 'refused' : 'disconnected',
        kickReason: status.kickReason,
      });
      if (statsCollectionArray.length > 0) {
        sendWebRtcStatsGatheredEvent(statsCollectionArray, 1000);
        realtime.sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
        statsCollectionArray = [];
      }
    }
    if (status.connectionState === 'connected') {
      playerConnectionState.updateState({ ...playerConnectionState.currentState, status: 'connected' });
    }
  };

  peer.status.addListener((status) => checkStatus(status));
  checkStatus(peer.status.currentState);

  setupLegacyTrackHandling();
  peer.onMessage((msg) => handleHostMessage(msg));
  setupLegacyStatsCollection(statsCollectionArray);

  return peer;
}

function setupLegacyTrackHandling(): void {
  let debounceTimeout: NodeJS.Timeout | null = null;
  peer.incomingTracks.addListener((tracks) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    const isAndroid = /Android/.test(navigator.userAgent);
    const mobile = isIOS || isAndroid;

    debounceTimeout = setTimeout(() => {
      sendVideoTransitTimingData('player_receive');
      const allTracks = Object.values(tracks).map((t) => t.track);
      const videoTracks = allTracks.filter((t) => t.kind === 'video');
      const audioTracks = allTracks.filter((t) => t.kind === 'audio');

      const currentStream = mediaStreamState.currentState.stream;
      const currentAudioStream = mediaStreamState.currentState.audioStream;

      if (mobile || !currentStream || !currentAudioStream) {
        const newStream = new MediaStream();
        const newAudioStream = new MediaStream();
        videoTracks.forEach((t) => newStream.addTrack(t));
        audioTracks.forEach((t) => { newStream.addTrack(t); newAudioStream.addTrack(t); });
        mediaStreamState.updateState({ stream: newStream, audioStream: newAudioStream });
      } else {
        const currentVideoTracks = currentStream.getVideoTracks();
        const currentAudioTracks = currentAudioStream.getAudioTracks();
        const videoChanged = currentVideoTracks.length !== videoTracks.length || !videoTracks.every((t) => currentVideoTracks.some((ct) => ct.id === t.id));
        const audioChanged = currentAudioTracks.length !== audioTracks.length || !audioTracks.every((t) => currentAudioTracks.some((ct) => ct.id === t.id));

        if (videoChanged || audioChanged) {
          if (videoChanged) {
            currentVideoTracks.forEach((t) => currentStream.removeTrack(t));
            videoTracks.forEach((t) => currentStream.addTrack(t));
          }
          if (audioChanged) {
            currentAudioTracks.forEach((t) => { currentStream.removeTrack(t); currentAudioStream.removeTrack(t); t.stop(); });
            audioTracks.forEach((t) => { currentStream.addTrack(t); currentAudioStream.addTrack(t); });
          }
          mediaStreamState.updateState({ stream: currentStream, audioStream: currentAudioStream });
        }
      }
      debounceTimeout = null;
    }, mobile ? 200 : 500);
  });
}

function setupLegacyStatsCollection(statsCollectionArray: WebrtcQualityStatsMetric[]): void {
  peer.statistics.addListener((stats) => {
    qualityManager.parseQualityStats(stats.quality);
    const rtpTimingData = extractPlayerRtpTimingData(stats.rtp);
    const combinedRawStats = { ...stats.quality.rawStats, ...rtpTimingData };

    statsCollectionArray.push({
      recommendation: stats.quality.recommendation,
      reason: stats.quality.reason,
      rawStats: combinedRawStats,
    });

    if (statsCollectionArray.length >= 10) {
      sendWebRtcStatsGatheredEvent(statsCollectionArray, 1000);
      realtime.sendMetricPlayerBatchWebrtcStats(statsCollectionArray, 1000);
      statsCollectionArray.length = 0;
    }
  });
}

const extractPlayerRtpTimingData = (rtpStats: { inbound: Array<Record<string, any>>; outbound: Array<Record<string, any>> }) => {
  const timingData: Record<string, number> = {};
  if (rtpStats.inbound && rtpStats.inbound.length > 0) {
    let found = false;
    for (const inbound of rtpStats.inbound) {
      if ((inbound.averageDecodeTimePerFrameMs && inbound.averageDecodeTimePerFrameMs > 0) ||
          (inbound.averageProcessingDelayPerFrameMs && inbound.averageProcessingDelayPerFrameMs > 0) ||
          (inbound.totalDecodeTime && inbound.totalDecodeTime > 0) ||
          (inbound.framesDecoded && inbound.framesDecoded > 0)) {
        timingData.averageDecodeTimePerFrameMs = inbound.averageDecodeTimePerFrameMs || 0;
        timingData.averageProcessingDelayPerFrameMs = inbound.averageProcessingDelayPerFrameMs || 0;
        timingData.totalDecodeTime = inbound.totalDecodeTime || 0;
        timingData.totalProcessingDelay = inbound.totalProcessingDelay || 0;
        timingData.framesDecoded = inbound.framesDecoded || 0;
        timingData.jitterBufferEmittedCount = inbound.jitterBufferEmittedCount || 0;
        found = true;
        break;
      }
    }
    if (!found) {
      timingData.averageDecodeTimePerFrameMs = 0;
      timingData.averageProcessingDelayPerFrameMs = 0;
      timingData.totalDecodeTime = 0;
      timingData.totalProcessingDelay = 0;
      timingData.framesDecoded = 0;
      timingData.jitterBufferEmittedCount = 0;
    }
  }
  return timingData;
};

export function getLegacyPeer(): PlaycastPeerClient | undefined {
  return peer;
}

export function closeLegacyPeer(reason: string): void {
  peer?.close(reason);
}

export function sendLegacyPeerMessage(message: any): void {
  if (peer) {
    peer.sendMessage(message);
  }
}

export function handleLegacyPeerMessage(message: any): void {
  peer?.handlePeerMessage(message);
}

export function devHardResetVideoLegacy(): void {
  if (peer) {
    const panicMessage = generators.peer.frameratePanic('player', peer.peerId, true);
    peer.sendMessage(panicMessage);
  }
}
